import { RootState } from '@/modules/zelos/store';
import { ActionContext, ActionTree } from 'vuex';
import { Mutations, MutationTypes } from './mutations';
import { State } from './state';
import {
  CEadVal,
  CEadValPos,
  Entwurf,
  GetEVDDocCompRequest,
  NEadSubPos,
  ExecuteExportRequest,
  NeadsubKennzeichenRequest,
  DocumentFile,
} from '../../../models/Zelos/ZelosModels';

// import { Actions as CommonActions, ActionTypes as CommonActionTypes } from '../../Common/actions';
import { useZelosCommonStore } from '../../zelosCommonStore';

import ApiService from '../../../services/ApiService';

export enum ActionTypes {
  GetEntwurfe = 'GETENTWURFE',
  GetNeadsubByEaddraftlocalrefnr = 'GETNEADSUBBYEADDRAFTLOCALREFNR',
  GetUpdateEntwurfe = 'GETUPDATEENTWURFE',
  SetKennzeichenNEadSub = 'SETKENNZEICHENNEADSUB',
  SendBezugszunnumer = 'SENDBEZUGSNUMMER',
  GetEVDDocComp = 'GETEVDDOCCOMP',
  GetNEadSubPosByNEadSubId = 'GETNEADSUBPOSBYNEADSUBID',
  GetCEadValDatum = 'GETCEADVALDATUM',
  GetUpdateCEadValDatum = 'GETUPDATECEADVALDATUM',
  GetCEadValPosByCEadValId = 'GETCEADVALPOSBYCEADVALID',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload?: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetEntwurfe](context: ActionArguments, datum: string): Promise<void>;
  [ActionTypes.GetNeadsubByEaddraftlocalrefnr](context: ActionArguments, data: string): Promise<void>;
  [ActionTypes.GetUpdateEntwurfe](context: ActionArguments, datum: string): Promise<void>;
  [ActionTypes.SetKennzeichenNEadSub](context: ActionArguments, data: NeadsubKennzeichenRequest): Promise<void>;
  [ActionTypes.SendBezugszunnumer](context: ActionArguments, data: ExecuteExportRequest): Promise<void>;
  [ActionTypes.GetEVDDocComp](context: ActionArguments, data: GetEVDDocCompRequest): Promise<void>;
  [ActionTypes.GetNEadSubPosByNEadSubId](context: ActionArguments, id: number): Promise<void>;
  [ActionTypes.GetCEadValDatum](context: ActionArguments, datum: string): Promise<void>;
  [ActionTypes.GetUpdateCEadValDatum](context: ActionArguments, datum: string): Promise<void>;
  [ActionTypes.GetCEadValPosByCEadValId](context: ActionArguments, id: number): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.GetEntwurfe]({ commit }: ActionArguments, datum: string) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, evdLoading: true, evdErrorMsg: '' });

    commit(MutationTypes.SetEntwurfe, new Array<Entwurf>());

    return ApiService.getAllEntwurfeDatum(datum)
      .then((res) => {
        const data: Array<Entwurf> = res.data;
        commit(MutationTypes.SetEntwurfe, data);
      })
      .catch((error) => {
        commonStore.status.evdErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.evdLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.GetUpdateEntwurfe]({ commit }: ActionArguments, datum: string) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, evdErrorMsg: '' });

    return ApiService.getAllEntwurfeDatum(datum)
      .then((res) => {
        const data: Array<Entwurf> = res.data;
        commit(MutationTypes.SetEntwurfe, data);
      })
      .catch((error) => {
        commonStore.status.evdErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.GetNeadsubByEaddraftlocalrefnr]({ dispatch }: ActionArguments, data: string) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, updateNeadsubLoading: true, neadsubErrorMsg: '' });

    return ApiService.getNeadsubByEaddraftlocalrefnr(data)
      .then((res) => {
        const data: Entwurf = res.data;
        dispatch(MutationTypes.UpdateNEadSub, data);
      })
      .catch((error) => {
        commonStore.status.neadsubErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.updateNeadsubLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.SetKennzeichenNEadSub]({ commit }: ActionArguments, data: NeadsubKennzeichenRequest) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, updateNeadsubLoading: true, neadsubErrorMsg: '' });

    return ApiService.neadsubEditKennzeichen(data)
      .then((res) => {
        const data: Entwurf = res.data;
        commit(MutationTypes.UpdateNEadSub, data);
      })
      .catch((error) => {
        commonStore.status.neadsubErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.updateNeadsubLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.SendBezugszunnumer]({}: ActionArguments, data: ExecuteExportRequest) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, updateNeadsubLoading: true, neadsubErrorMsg: '' });

    return ApiService.executeExport(data)
      .then((_res) => {})
      .catch((error) => {
        commonStore.status.neadsubErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.updateNeadsubLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.GetEVDDocComp]({ commit }: ActionArguments, data: GetEVDDocCompRequest) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, evdCompLoading: true, evdErrorMsg: '' });

    commit(MutationTypes.SetNEadSubDoc, null);

    return ApiService.getEVDDocComp(data)
      .then(async (res) => {
        const base64String = await blobToBase64(res.data);
        const doc = new DocumentFile(res.data.size, base64String);
        commit(MutationTypes.SetNEadSubDoc, doc);
      })
      .catch(async (error) => {
        commonStore.status.evdErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.evdCompLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.GetNEadSubPosByNEadSubId]({ commit }: ActionArguments, id: number) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, getNeadsubposLoading: true, neadsubposErrorMsg: '' });

    return ApiService.getNEadSubPosByNEadSubId(id)
      .then((res) => {
        const data: Array<NEadSubPos> = res.data;
        commit(MutationTypes.AddNEadSubPosRange, data);
      })
      .catch((error) => {
        commonStore.status.neadsubposErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.getNeadsubposLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.GetCEadValDatum]({ commit }: ActionArguments, datum: string) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, getCeadvalLoading: true, ceadvalErrorMsg: '' });

    return ApiService.getCEadValDatum(datum)
      .then((res) => {
        const data: Array<CEadVal> = res.data;
        commit(MutationTypes.SetCEadVal, data);
      })
      .catch((error) => {
        commonStore.status.ceadvalErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.getCeadvalLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.GetUpdateCEadValDatum]({ commit }: ActionArguments, datum: string) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, ceadvalErrorMsg: '' });

    return ApiService.getCEadValDatum(datum)
      .then((res) => {
        const data: Array<CEadVal> = res.data;
        commit(MutationTypes.SetCEadVal, data);
      })
      .catch((error) => {
        commonStore.status.ceadvalErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.setStatus(commonStore.status);
      });
  },

  async [ActionTypes.GetCEadValPosByCEadValId]({ commit }: ActionArguments, id: number) {
    const commonStore = useZelosCommonStore();
    commonStore.setStatus({ ...commonStore.status, getCeadvalposLoading: true, ceadvalposErrorMsg: '' });

    return ApiService.getCEadValPosByCEadValId(id)
      .then((res) => {
        const data: Array<CEadValPos> = res.data;
        commit(MutationTypes.AddCEadValPosRange, data);
      })
      .catch((error) => {
        commonStore.status.ceadvalposErrorMsg = error.response;
      })
      .finally(() => {
        commonStore.status.getCeadvalposLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  },
};

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result.split(',')[1]); // Strip out the data URL part
      } else {
        reject('Failed to convert blob to base64 string');
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
